import WidthDetector from '@atlaskit/width-detector';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { defineMessages, FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import PageContentLoaded from '../../apdex/PageContentLoaded';
import { getConfig } from '../../config';
import { JoinSiteExperimentConsumerProps, withJoinSiteExperiment } from '../../experiments/join-site';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import { PageContent } from '../../model/PageContent';
import Store from '../../store';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import ProductStore from '../../store/productStore';
import UIStore from '../../store/uiStore';
import UserStore from '../../store/userStore';
import CustomFeedback from '../CustomFeedback';
import CardRow, { Header } from '../Products/CardRow';
import { getHelpfulLinks } from '../Products/LinkCard/helpfulLinks';
import { getWacProductLinks } from '../Products/TryProductCard/wacProductLinks';
import { getVisibleProductsCount } from '../Products/util';

type InjectedProps = {
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
  userStore: UserStore;
} & AnalyticsConsumerProps &
  JoinSiteExperimentConsumerProps;

type Props = {
  containerWidth: number;
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
  uiStore: UIStore;
} & InjectedProps;

const { atlassianAccountUrl } = getConfig();

const EmptyStateContainer = styled.div`
  min-height: 100vh;
  position: relative;
  box-sizing: border-box;
`;

const FeedbackContainer = styled.div`
  display: flex;
  justify-content: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding-bottom: 16px;
`;

const ButtonSeparator = styled.span`
  margin: 0 8px;
`;

@observer
class EmptyState extends React.Component<Props> {
  render() {
    const { containerWidth, joinSiteExperiment, userStore } = this.props;
    const { flags } = this.props.featureFlagsStore;
    const { joinableSites, availableSites, hasLoaded } = this.props.productStore;
    const { mobileMode } = this.props.uiStore;

    if (!hasLoaded || availableSites.length > 0 || (flags['uchi.show-joinable-sites'] && joinableSites.length > 0)) {
      return null;
    }

    const links = getHelpfulLinks();
    const tryProducts = getWacProductLinks();
    const maxCardsPerRow = getVisibleProductsCount(containerWidth, mobileMode);
    if (joinSiteExperiment.isVariation) {
      return (
        <EmptyStateContainer data-test-id="empty-state">
          <PageContentLoaded emptyState pageContent={PageContent.PRODUCTS} />
          <div>
            <Header>
              <FormattedMessage {...messages.productsHeader} />
            </Header>
            <p>
              <FormattedHTMLMessage
                {...messages.emptyStateBody}
                values={{
                  email: userStore.email,
                  ourProductsLink: 'https://www.atlassian.com/try',
                  tryDifferentAccountLink: `${atlassianAccountUrl}/login/select-account?application=wac`,
                }}
              />
            </p>
            <p>
              <a href={`${atlassianAccountUrl}/login/select-account?application=wac`}>
                <FormattedMessage {...messages.tryDifferentAccount} />
              </a>
              <ButtonSeparator>|</ButtonSeparator>
              <a href="https://www.atlassian.com/try">
                <FormattedMessage {...messages.tryFreeProducts} />
              </a>
            </p>
          </div>
          <CardRow
            header={<FormattedMessage {...messages.helpfulLinksHeader} />}
            links={links}
            maxCardsPerRow={maxCardsPerRow}
          />
        </EmptyStateContainer>
      );
    } else {
      return (
        <EmptyStateContainer data-test-id="empty-state">
          <PageContentLoaded emptyState pageContent={PageContent.PRODUCTS} />
          <CardRow
            header={<FormattedMessage {...messages.helpfulLinksHeader} />}
            links={links}
            maxCardsPerRow={maxCardsPerRow}
          />
          <CardRow
            header={<FormattedMessage {...messages.ourProductsHeader} />}
            tryProducts={tryProducts}
            maxCardsPerRow={maxCardsPerRow}
          />
          <FeedbackContainer>
            <CustomFeedback />
          </FeedbackContainer>
        </EmptyStateContainer>
      );
    }
  }
}

export const InjectedEmptyState = inject(({ featureFlagsStore, productStore, uiStore, userStore }: Store) => ({
  featureFlagsStore,
  productStore,
  uiStore,
  userStore,
}))(withAnalytics(withJoinSiteExperiment(EmptyState)));

export default () => (
  <WidthDetector>{(width?: number) => <InjectedEmptyState containerWidth={width || 0} />}</WidthDetector>
);

const messages = defineMessages({
  emptyStateBody: {
    defaultMessage:
      'It looks like you don’t have access to any products on your account {email}.<br />' +
      'If you need to join your team, <b>ask your site admin for an invitation.</b>',
    description: 'Body of empty state container with links to relevant actions',
    id: 'uchi.emptyStateBody',
  },
  helpfulLinksHeader: {
    defaultMessage: 'Helpful links',
    description: 'Helpful links header',
    id: 'uchi.helpfulLinksHeaderEmptyState',
  },
  ourProductsHeader: {
    defaultMessage: 'Our products',
    description: 'Our products header',
    id: 'uchi.ourProductsHeader',
  },
  productsHeader: {
    defaultMessage: 'You don’t have access to any Atlassian products',
    description: 'Products header',
    id: 'uchi.productsHeaderEmptyState',
  },
  tryDifferentAccount: {
    defaultMessage: 'Try a different account',
    description: 'A button title that asks the user to try signing in with a different account',
    id: 'uchi.emptyState.tryDifferentAccount',
  },
  tryFreeProducts: {
    defaultMessage: 'Try Atlassian products for free',
    description: 'A button title that asks the user to try free Atlassian products',
    id: 'uchi.emptyState.tryFreeProducts',
  },
});
